import { Component } from '@angular/core';
import { ItemEntry, SEARCH_TYPE, searchtypes } from './core/quotes.model';
import { FormBuilder, FormGroup } from '@angular/forms';
import { CustomValidatorService } from '../../../components/form-elements/custom-validator.service';
import { firstValueFrom, Subscription, take } from 'rxjs';
import { ToasterService } from '../../../components/toaster/toaster.service';
import { TOASTER_TYPE } from '../../../components/toaster/toaster.model';
import { QuoteListService } from './components/quotation-form/core/quote-list.service';
import { isEmpty } from '../../../core/services/simple-tools';
import { WindowRef, TranslationService } from '@spartacus/core';
import { FileConversionService } from '../../../core/services/file-conversion-service.service';
import { QuotesService } from './core/quotes.service';
import { QuoteProductListService } from './components/product-finder/product-list/core/quote-product-list.service';
import { CustomFormValidators } from '@spartacus/storefront';

@Component({
  selector: 'quotes',
  templateUrl: './quotes.component.html',
  styleUrl: './quotes.component.scss',
  standalone: false,
})
export class QuotesComponent {
  SEARCH_TYPE = SEARCH_TYPE;
  searchtypes = searchtypes;
  subscription = new Subscription();
  currentSearchType: SEARCH_TYPE | string = SEARCH_TYPE.BASIC;

  formGeneralInformations?: FormGroup = this.fb.group({
    country: this.fb.group({
      isocode: [null, this.customValidatorService.required()],
    }),
    emailId: ['', [CustomFormValidators.emailValidator, this.customValidatorService.required()]],
    endCustomer: ['', []],
    competitor: ['', [this.customValidatorService.required()]],
    additionalInformation: ['', []],
    additionalAttachment: ['', []],
  });

  constructor(
    protected fb: FormBuilder,
    private customValidatorService: CustomValidatorService,
    private toasterService: ToasterService,
    private quoteListService: QuoteListService,
    private winref: WindowRef,
    private fileConversionService: FileConversionService,
    private quotesService: QuotesService,
    private quoteProductListService: QuoteProductListService,
    private translationService: TranslationService,
  ) {}

  setSearchType(type: SEARCH_TYPE | string) {
    this.currentSearchType = type;
  }

  checkEntries(entries: ItemEntry[]) {
    let isOk = true;

    if (this.winref.isBrowser()) {
      const $table = this.winref.document.querySelector('.js-table-quotes') as HTMLDivElement;
      const inputClasses = ['ng-invalid', 'ng-touched'];

      entries.forEach(entry => {
        const $qty = $table.querySelector(`.js-qty[data-entry="${entry.code}"]`) as HTMLInputElement;
        const $reqPrice = $table.querySelector(`.js-req-price[data-entry="${entry.code}"]`) as HTMLInputElement;
        inputClasses.forEach(_class => $qty.classList.remove(_class));

        $qty.classList.remove('ng-touched');
        $reqPrice.classList.remove('ng-touched');

        if (entry.qty === 0 || isEmpty(entry.qty?.toString())) {
          inputClasses.forEach(_class => $qty.classList.add(_class));
          isOk = isOk && false;
        }

        if (isEmpty(entry.reqPrice)) {
          inputClasses.forEach(_class => $reqPrice.classList.add(_class));
          isOk = isOk && false;
        }
      });
    }

    return isOk;
  }

  async validateQuote(): Promise<boolean> {
    this.toasterService.closeALL();
    this.formGeneralInformations?.markAllAsTouched();
    const entries: ItemEntry[] = this.quoteListService.entries$.getValue();

    if (!entries.length) {
      const noProductSelected = await firstValueFrom(
        this.translationService.translate('quotes.validation.noProductSelected'),
      );
      this.toasterService.init({
        type: TOASTER_TYPE.DANGER,
        content: `<strong>${noProductSelected}</strong>`,
      });

      return false;
    }

    if (!this.checkEntries(entries)) {
      const invalidFields = await firstValueFrom(this.translationService.translate('quotes.validation.invalidFields'));
      this.toasterService.init({
        type: TOASTER_TYPE.DANGER,
        content: invalidFields,
      });

      return false;
    }

    if (!this.formGeneralInformations?.valid) {
      const invalidGeneralInfo = await firstValueFrom(
        this.translationService.translate('quotes.validation.invalidGeneralInfo'),
      );
      this.toasterService.init({
        type: TOASTER_TYPE.DANGER,
        content: invalidGeneralInfo,
      });

      return false;
    }

    return true;
  }

  formatEntries(): ItemEntry[] {
    const entries: ItemEntry[] = JSON.parse(JSON.stringify(this.quoteListService.entries$.getValue()));

    return (entries || []).map(entry => {
      if (!entry.product?.isCustom) {
        entry.product = {
          isCustom: entry.product?.isCustom || false,
          code: entry.product?.code,
        };
      }

      entry.reqPrice = (entry.reqPrice || '').replace(/[^0-9.,]/g, '');
      return entry;
    });
  }

  async formatAttachments() {
    const attachments: File[] = this.formGeneralInformations?.get('additionalAttachment')?.value;

    if (attachments?.length) {
      return await this.fileConversionService.convertFilesToBase64(attachments);
    }

    return [];
  }

  resetAll() {
    this.formGeneralInformations?.reset();
    this.formGeneralInformations?.get('country')?.get('isocode')?.setValue('');
    this.quoteListService.entries$.next([]);
    this.quoteProductListService.showProducts$.next(false);
    this.quotesService.clearBasicSearch$.emit();
    this.quotesService.clearFileList$.emit();
    this.quotesService.resetGeneralInformation$.emit();
  }

  async submitQuote() {
    this.formGeneralInformations?.markAllAsTouched();
    const entries = this.formatEntries();
    const attachments = await this.formatAttachments();
    const isValidForm = await this.validateQuote();

    if (!isValidForm) {
      return;
    }

    const form = {
      ...this.formGeneralInformations?.value,
      ...{ additionalAttachment: attachments },
      ...{ entries },
    };

    this.quotesService
      .postQuote(form)
      .pipe(take(1))
      .subscribe(response => {
        this.toasterService.init({
          type: response.hasError ? TOASTER_TYPE.DANGER : TOASTER_TYPE.SUCCESS,
          content: response.message!,
        });

        if (response.hasSuccess) {
          this.resetAll();
        }
      });
  }
}
